// src/core/headWidth.js
//
// Per-user head width relative to the canonical face model.
//
// The pose solver fits the canonical face with a FIXED size, so a user whose
// head is wider than the average shows up as landmarks that sit OUTSIDE the
// pose-predicted positions at the face sides. We measure that directly: for
// pairs of face-oval landmarks at the cheekbone / temple level, compare the
// observed horizontal distance on screen with the distance the current pose
// predicts for the canonical vertices. ratio > 1 → wider head than canonical.
//
// Used by Glasses.setTempleSplay(): the temple arms are rotated outward about
// their hinges so the frame hugs the real head instead of passing through the
// occluder (the "broken temple" / "sinks into the cheek at yaw" artefacts).

import { CANONICAL_FACE_VERTICES as cv } from '../web/canonicalFace.js';

// Left/right pairs on the face oval, cheekbone to temple level.
const PAIRS = [[234, 454], [127, 356], [93, 323], [132, 361], [162, 389]];

/**
 * @param {Array<{x,y}>} landmarks   normalised
 * @param {Float32Array|number[]} m  column-major pose (canonical → camera, cm)
 * @param {number} f                 1 / tan(vfov/2)
 * @param {number} aspect            w / h
 * @returns {number|null} observed / predicted width ratio for this frame
 */
export function measureHeadWidthRatio(landmarks, m, f, aspect) {
  if (!landmarks || landmarks.length < 468 || !m) return null;
  const fx = f / aspect;
  let num = 0, den = 0;
  for (const [a, b] of PAIRS) {
    const la = landmarks[a], lb = landmarks[b];
    if (!la || !lb) continue;
    const ua = projX(a, m, fx), ub = projX(b, m, fx);
    if (ua == null || ub == null) continue;
    const dPred = Math.abs(ub - ua);
    const dObs = Math.abs(lb.x - la.x) * 2;          // normalised → NDC span
    if (dPred < 0.05) continue;
    num += dObs; den += dPred;
  }
  return den > 0 ? num / den : null;
}

function projX(i, m, fx) {
  const px = cv[i*3], py = cv[i*3+1], pz = cv[i*3+2];
  const X = m[0]*px + m[4]*py + m[8]*pz + m[12];
  const Z = m[2]*px + m[6]*py + m[10]*pz + m[14];
  if (Z > -1) return null;
  return fx * X / -Z;
}

export class HeadWidthEstimator {
  constructor({ lockAfter = 40, min = 0.85, max = 1.3 } = {}) {
    this.lockAfter = lockAfter; this.min = min; this.max = max;
    this.value = 1; this.samples = 0; this.locked = false;
  }
  push(landmarks, m, f, aspect) {
    if (this.locked) return false;
    const r = measureHeadWidthRatio(landmarks, m, f, aspect);
    if (r == null) return false;
    const c = Math.max(this.min, Math.min(this.max, r));
    this.value = this.samples === 0 ? c : this.value + (c - this.value) * 0.1;
    this.samples++;
    if (this.samples >= this.lockAfter) this.locked = true;
    return true;
  }
}
